import { BranchIdentifierSystem } from "@/api-schema-enums";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Fingerprint } from "lucide-react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { BranchIdentifierFormValues, isDuplicateBranchIdentifier } from "../branch-identifiers";

interface BranchFormWithIdentifiers {
  branchIdentifiers: BranchIdentifierFormValues;
}

interface BranchIdentifierFormCardProps {
  duplicateKeys?: ReadonlySet<string>;
  isCheckingDuplicates?: boolean;
}

const fixedIdentifierFields = [
  {
    name: "branchIdentifiers.v1",
    type: BranchIdentifierSystem.V1,
    labelKey: "masterdata:V1ID",
    placeholderKey: "masterdata:V1IdExample",
  },
  {
    name: "branchIdentifiers.formel1Ahg",
    type: BranchIdentifierSystem.Formel1Ahg,
    labelKey: "masterdata:Formel1AhgIdentifier",
    placeholderKey: "masterdata:BranchIdentifierExample",
  },
  {
    name: "branchIdentifiers.formel1Ghg",
    type: BranchIdentifierSystem.Formel1Ghg,
    labelKey: "masterdata:Formel1GhgIdentifier",
    placeholderKey: "masterdata:BranchIdentifierExample",
  },
  {
    name: "branchIdentifiers.cross",
    type: BranchIdentifierSystem.CROSS,
    labelKey: "masterdata:CrossIdentifier",
    placeholderKey: "masterdata:BranchIdentifierExample",
  },
  {
    name: "branchIdentifiers.fahrschule",
    type: BranchIdentifierSystem.FAHRSCHULE,
    labelKey: "masterdata:FahrschuleIdentifier",
    placeholderKey: "masterdata:FahrschuleIdentifierExample",
  },
  {
    name: "branchIdentifiers.carano",
    type: BranchIdentifierSystem.CARANO,
    labelKey: "masterdata:CaranoIdentifier",
    placeholderKey: "masterdata:BranchIdentifierExample",
  },
  {
    name: "branchIdentifiers.sap",
    type: BranchIdentifierSystem.Sap,
    labelKey: "masterdata:SapIdentifier",
    placeholderKey: "masterdata:BranchIdentifierExample",
  },
] as const;

export function BranchIdentifierFormCard({
  duplicateKeys = new Set(),
  isCheckingDuplicates = false,
}: BranchIdentifierFormCardProps) {
  const { t } = useTranslation(["masterdata", "common"]);
  const { control } = useFormContext<BranchFormWithIdentifiers>();

  return (
    <Card className="shadow-none">
      <CardHeader className="pb-4">
        <CardTitle className="flex items-center gap-2">
          <Fingerprint className="h-4 w-4" />
          {t("masterdata:BranchIdentifiers")}
        </CardTitle>
        <CardDescription>{t("masterdata:BranchIdentifiersDescription")}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
          {fixedIdentifierFields.map((identifierField) => (
            <FormField
              key={identifierField.type}
              control={control}
              name={identifierField.name}
              render={({ field }) => {
                const hasDuplicate = isDuplicateBranchIdentifier(duplicateKeys, identifierField.type, field.value);

                return (
                  <FormItem>
                    <FormLabel>{t(identifierField.labelKey)}</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        value={field.value ?? ""}
                        placeholder={t(identifierField.placeholderKey)}
                        inputMode={
                          identifierField.type === BranchIdentifierSystem.V1 ||
                          identifierField.type === BranchIdentifierSystem.Formel1Ahg ||
                          identifierField.type === BranchIdentifierSystem.Formel1Ghg ||
                          identifierField.type === BranchIdentifierSystem.CROSS ||
                          identifierField.type === BranchIdentifierSystem.Sap
                            ? "numeric"
                            : undefined
                        }
                        error={hasDuplicate}
                      />
                    </FormControl>
                    <FormMessage />
                    {hasDuplicate ? (
                      <p className="text-error text-sm">{t("masterdata:BranchIdentifierAlreadyExists")}</p>
                    ) : null}
                  </FormItem>
                );
              }}
            />
          ))}
        </div>

        {isCheckingDuplicates ? (
          <p className="text-muted-foreground text-sm">{t("masterdata:CheckingDuplicateBranchIdentifiers")}</p>
        ) : null}
      </CardContent>
    </Card>
  );
}
